import { useAuth0 } from "@auth0/auth0-react";
import { CssBaseline, ThemeProvider as MuiThemeProvider } from "@mui/material";
import React, { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useParams, useSearchParams } from "react-router-dom";

import ReloadPrompt from "components/common/ReloadPrompt";
import { ThemeProvider } from "components/products/muggleTestV0/components/theme-provider";
import { DEFAULT_LOCALE } from "constants/i18n";
import { PRICING_CASES_ADMIN_PATH } from "constants/organization/pricingFeedbackPaths";
import {
  ADMIN_PARTNER_STATEMENTS_TITLE,
  ADMIN_PARTNERS_TITLE,
  ADMIN_REFERRALS_TITLE,
  ADMIN_SOURCE_OF_SALES_TITLE,
  PARTNER_PROGRAM_TITLE,
} from "constants/partner/partnerCopy";
import {
  ADMIN_PARTNER_STATEMENTS_PATH,
  ADMIN_PARTNERS_PATH,
  ADMIN_REFERRALS_PATH,
  ADMIN_SOURCE_OF_SALES_PATH,
  PARTNER_PAGE_PATH,
} from "constants/partner/partnerRoutes";
import { ADMIN_SUBSCRIPTIONS_TITLE } from "constants/subscription/adminSubscriptionsCopy";
import { ADMIN_SUBSCRIPTIONS_PATH } from "constants/subscription/adminSubscriptionsPaths";
import { COMMON_COPY } from "copy/common";
import { useVersionWatcher } from "hooks/useVersionWatcher";
import { createCopyResolver } from "i18n/resolveCopy";
import { useCopy } from "i18n/useCopy";
import { CAMPAIGN_ICON_PATH } from "products/charity/constants/campaignBrand";
import { CAMPAIGN_HOME_SCREEN_TITLE, CAMPAIGN_MANIFEST_PATH } from "products/charity/constants/campaignInstall";
import { CHARITY_REPORT_ACTION_LABEL, CHARITY_RIDES_LABEL } from "products/charity/constants/charityActionLabels";
import { CharityLayout } from "products/charity/layout";
import { TestCasesRedirect } from "products/muggleTestV0/dashboard/projects/[id]/testcases/components/TestCasesRedirect";
import { MuggleTestLayout } from "products/muggleTestV0/layout";
import { MainPageV2 } from "products/muggleTestV0/main-page-v2";
import { type ICopyResolver } from "types/i18n/ICopyResolver";
import { reloadWithLatestServiceWorker } from "utils/serviceWorkerUpdate";

import ProtectedRoute from "./components/auth/ProtectedRoute";
import { BackendUnavailablePage } from "./components/common/BackendUnavailablePage";
import ErrorBoundary from "./components/common/ErrorBoundary";
import PageLoader from "./components/common/PageLoader";
import MasterLayout from "./components/master/MasterLayout";
import { SuspendedOrgRedirect } from "./components/organization/SuspendedOrgRedirect";
import { AdminRoute } from "./components/products/muggleTestV0/components/AdminRoute";
import { PartnerRoute } from "./components/products/muggleTestV0/components/PartnerRoute";
import { GOOGLE_ANALYTICS_MEASUREMENT_ID } from "./constants/analyticsConfig";
import { useUserSessionInitializer } from "./hooks/useUserSessionInitializer";
import { pushPageViewDataLayerEvent } from "./lib/gtmDataLayer";
import CallbackPage from "./pages/main-layout/Callback";
import Terms from "./pages/main-layout/Terms";
import { archivedRoutes, ARCHIVED_TITLE_RULES } from "./routes/archived-routes";
import { type ITitleRule } from "./routes/types";
import { darkTheme } from "./themes/theme";

// Lazy load pages for better performance
const AccountPage = lazy(() => import("products/muggleTestV0/dashboard/account/page"));
const AnalyticsPage = lazy(() => import("products/muggleTestV0/dashboard/analytics/page"));
const BillingPage = lazy(() => import("products/muggleTestV0/dashboard/billing/page"));
const DashboardLayout = lazy(() => import("products/muggleTestV0/dashboard/layout"));
const DashboardPage = lazy(() => import("products/muggleTestV0/dashboard/page"));
const HealthPage = lazy(() => import("products/muggleTestV0/health/page"));
const ProfilePage = lazy(() => import("products/muggleTestV0/dashboard/profile/page"));
const ProjectDetailPage = lazy(() => import("products/muggleTestV0/dashboard/projects/[id]/page"));
const LegacyProjectReportPage = lazy(() => import("products/muggleTestV0/dashboard/projects/[id]/report/page"));
const ProjectSchedulesPage = lazy(() => import("products/muggleTestV0/dashboard/projects/[id]/schedules/page"));
const ProjectTestcasesPage = lazy(() => import("products/muggleTestV0/dashboard/projects/[id]/testcases/page"));
const TestReportPage = lazy(() => import("./pages/test-report/test-report-page"));
const CreateProjectLayout = lazy(() => import("products/muggleTestV0/dashboard/projects/create/layout"));
const CreateWebsitePage = lazy(() => import("products/muggleTestV0/dashboard/projects/create/website/page"));
const CreateRequirementsPage = lazy(() => import("products/muggleTestV0/dashboard/projects/create/requirements/page"));
const CreatePrdPage = lazy(() => import("products/muggleTestV0/dashboard/projects/create/prd/page"));
const CreateCredentialsPage = lazy(() => import("products/muggleTestV0/dashboard/projects/create/credentials/page"));
const CreateProgressPage = lazy(() => import("products/muggleTestV0/dashboard/projects/create/progress/page"));
const ProjectsPage = lazy(() => import("products/muggleTestV0/dashboard/projects/page"));
const SettingsPage = lazy(() => import("products/muggleTestV0/dashboard/settings/page"));
const SupportPage = lazy(() => import("products/muggleTestV0/support/page"));
const SchedulePage = lazy(() => import("products/muggleTestV0/dashboard/schedule/page"));
const InvitePage = lazy(() => import("products/muggleTestV0/invite/page"));
const DocsPage = lazy(() => import("products/muggleTestV0/docs/page"));
const PartnerPage = lazy(() => import("products/muggleTestV0/dashboard/partner/page"));
const AdminUsersPage = lazy(() => import("products/muggleTestV0/dashboard/admin/users/page"));
const AdminGrantTrialPage = lazy(() => import("products/muggleTestV0/dashboard/admin/grant-trial/page"));
const AdminPartnersPage = lazy(() => import("products/muggleTestV0/dashboard/admin/partners/page"));
const AdminReferralsPage = lazy(() => import("products/muggleTestV0/dashboard/admin/referrals/page"));
const AdminPartnerStatementsPage = lazy(() => import("products/muggleTestV0/dashboard/admin/partner-statements/page"));
const OrganizationOverviewPage = lazy(() => import("products/muggleTestV0/dashboard/organization/page"));
const OrganizationMembersPage = lazy(() => import("products/muggleTestV0/dashboard/organization/members/page"));
const OrganizationCapacityPage = lazy(() => import("products/muggleTestV0/dashboard/organization/capacity/page"));
const OrganizationBillingPage = lazy(() => import("products/muggleTestV0/dashboard/organization/billing/page"));
const OrganizationUsagePage = lazy(() => import("products/muggleTestV0/dashboard/organization/usage/page"));
const OrgInviteAcceptPage = lazy(() => import("products/muggleTestV0/org-invite/page"));
const ProjectTransferLandingPage = lazy(() => import("products/muggleTestV0/project-transfer/page"));
const AdminSourceOfSalesPage = lazy(() => import("products/muggleTestV0/dashboard/admin/source-of-sales/page"));
const AdminProjectTransfersPage = lazy(() => import("products/muggleTestV0/dashboard/admin/project-transfers/page"));
const AdminPricingCasesPage = lazy(() => import("products/muggleTestV0/dashboard/admin/pricing-cases/page"));
const AdminSubscriptionsPage = lazy(() => import("products/muggleTestV0/dashboard/admin/subscriptions/page"));
const EnterpriseBillingPage = lazy(() => import("products/muggleTestV0/billing/page"));

// Charity pages
const CharityCampaignPage = lazy(() =>
  import("products/charity/pages/campaign").then((m) => ({ default: m.CampaignPage }))
);
const CharityJoinPage = lazy(() => import("products/charity/pages/join").then((m) => ({ default: m.JoinPage })));
const CharitySignInPage = lazy(() => import("products/charity/pages/signin").then((m) => ({ default: m.SignInPage })));
const CharityAuthPage = lazy(() => import("products/charity/pages/auth").then((m) => ({ default: m.AuthPage })));
const CharityReportPage = lazy(() => import("products/charity/pages/report").then((m) => ({ default: m.ReportPage })));
const CharityReportsPage = lazy(() =>
  import("products/charity/pages/reports").then((m) => ({ default: m.ReportsPage }))
);
const CharityHouseholdPage = lazy(() =>
  import("products/charity/pages/household").then((m) => ({ default: m.HouseholdPage }))
);
const CharityLeaderboardPage = lazy(() =>
  import("products/charity/pages/leaderboard").then((m) => ({ default: m.LeaderboardPage }))
);
const CharityHowItWorksPage = lazy(() =>
  import("products/charity/pages/how-it-works").then((m) => ({ default: m.HowItWorksPage }))
);
const CharityReportCorrectPage = lazy(() =>
  import("products/charity/pages/report-correct").then((m) => ({ default: m.ReportCorrectPage }))
);
const CharityHowToJoinPage = lazy(() =>
  import("products/charity/pages/how-to-join").then((m) => ({ default: m.HowToJoinPage }))
);
const CharityHowToScreenshotAppsPage = lazy(() =>
  import("products/charity/pages/how-to-screenshot-apps").then((m) => ({ default: m.HowToScreenshotAppsPage }))
);
const CharityHowToReportPage = lazy(() =>
  import("products/charity/pages/how-to-report").then((m) => ({ default: m.HowToReportPage }))
);
const CharityHowToGroupRidePage = lazy(() =>
  import("products/charity/pages/how-to-group-ride").then((m) => ({ default: m.HowToGroupRidePage }))
);
const CharityRideTogetherPage = lazy(() =>
  import("products/charity/pages/ride-together").then((m) => ({ default: m.RideTogetherPage }))
);
const CharityRulesPage = lazy(() => import("products/charity/pages/rules").then((m) => ({ default: m.RulesPage })));
const CharityPrivacyPage = lazy(() =>
  import("products/charity/pages/privacy").then((m) => ({ default: m.PrivacyPage }))
);
const CharityAdminQueuePage = lazy(() =>
  import("products/charity/pages/admin-queue").then((m) => ({ default: m.AdminQueuePage }))
);
const CharityAdminReportDetailPage = lazy(() =>
  import("products/charity/pages/admin-report-detail").then((m) => ({ default: m.AdminReportDetailPage }))
);
const CharityAdminExportPage = lazy(() =>
  import("products/charity/pages/admin-export").then((m) => ({ default: m.AdminExportPage }))
);

const APP_NAME = "Muggle Test";
const APP_ICON_PATH = "/muggleTestV0/public/images/muggleai-square-avatar-white-with-text.png";
const APP_ICON_TYPE = "image/png";
const APPLE_TOUCH_ICON_REL = "apple-touch-icon";
const FAVICON_ID = "muggle-app-favicon";
const FAVICON_REL = "icon";
const SHORTCUT_ICON_REL = "shortcut icon";
const CHARITY_ROUTE_PREFIX = "/charity";
const APP_MANIFEST_PATH = "/manifest.json";
const MANIFEST_REL = "manifest";
const APPLE_WEB_APP_TITLE_NAME = "apple-mobile-web-app-title";

const TITLE_RULES: ITitleRule[] = [
  ...ARCHIVED_TITLE_RULES,
  { test: (pathname: string) => pathname === "/callback", title: COMMON_COPY.pageTitles.signInCallback },
  { test: (pathname: string) => pathname === "/muggleTestV0", title: COMMON_COPY.pageTitles.home },
  { test: (pathname: string) => pathname === "/muggleTestV0/landing", title: COMMON_COPY.pageTitles.landing },
  { test: (pathname: string) => pathname === "/muggleTestV0/invite", title: COMMON_COPY.pageTitles.invite },
  { test: (pathname: string) => pathname === "/muggleTestV0/support", title: COMMON_COPY.pageTitles.support },
  { test: (pathname: string) => pathname === "/muggleTestV0/docs", title: COMMON_COPY.pageTitles.docs },
  {
    test: (pathname: string) => /^\/muggleTestV0\/docs\/[^/]+\/[^/]+$/.test(pathname),
    title: COMMON_COPY.pageTitles.docsArticle,
  },
  { test: (pathname: string) => pathname === "/muggleTestV0/terms", title: COMMON_COPY.pageTitles.termsAndPrivacy },
  { test: (pathname: string) => pathname === "/muggleTestV0/health", title: COMMON_COPY.pageTitles.serviceStatus },
  { test: (pathname: string) => pathname === "/muggleTestV0/dashboard", title: COMMON_COPY.pageTitles.dashboard },
  {
    test: (pathname: string) => pathname === "/muggleTestV0/dashboard/settings",
    title: COMMON_COPY.pageTitles.settings,
  },
  {
    test: (pathname: string) => pathname === "/muggleTestV0/dashboard/projects",
    title: COMMON_COPY.pageTitles.projects,
  },
  {
    test: (pathname: string) => /^\/muggleTestV0\/dashboard\/projects\/[^/]+$/.test(pathname),
    title: COMMON_COPY.pageTitles.projectDetails,
  },
  {
    test: (pathname: string) => pathname === "/muggleTestV0/dashboard/projects/create/website",
    title: COMMON_COPY.pageTitles.createProjectWebsite,
  },
  {
    test: (pathname: string) => pathname === "/muggleTestV0/dashboard/projects/create/requirements",
    title: COMMON_COPY.pageTitles.createProjectRequirements,
  },
  {
    test: (pathname: string) => pathname === "/muggleTestV0/dashboard/projects/create/prd",
    title: COMMON_COPY.pageTitles.createProjectPrd,
  },
  {
    test: (pathname: string) => pathname === "/muggleTestV0/dashboard/projects/create/credentials",
    title: COMMON_COPY.pageTitles.createProjectCredentials,
  },
  {
    test: (pathname: string) => pathname === "/muggleTestV0/dashboard/projects/create/progress",
    title: COMMON_COPY.pageTitles.createProjectProgress,
  },
  {
    test: (pathname: string) => /^\/muggleTestV0\/dashboard\/projects\/[^/]+\/testcases$/.test(pathname),
    title: COMMON_COPY.pageTitles.projectTestCases,
  },
  {
    test: (pathname: string) => /^\/muggleTestV0\/dashboard\/projects\/[^/]+\/scripts$/.test(pathname),
    title: COMMON_COPY.pageTitles.projectScripts,
  },
  {
    test: (pathname: string) => /^\/muggleTestV0\/dashboard\/projects\/[^/]+\/runs$/.test(pathname),
    title: COMMON_COPY.pageTitles.projectRuns,
  },
  {
    test: (pathname: string) => /^\/muggleTestV0\/dashboard\/projects\/[^/]+\/report$/.test(pathname),
    title: COMMON_COPY.pageTitles.projectReport,
  },
  {
    test: (pathname: string) => /^\/muggleTestV0\/dashboard\/projects\/[^/]+\/schedules$/.test(pathname),
    title: COMMON_COPY.pageTitles.projectSchedules,
  },
  { test: (pathname: string) => pathname === "/muggleTestV0/dashboard/profile", title: COMMON_COPY.pageTitles.profile },
  { test: (pathname: string) => pathname === "/muggleTestV0/dashboard/billing", title: COMMON_COPY.pageTitles.billing },
  {
    test: (pathname: string) => pathname === "/muggleTestV0/dashboard/analytics",
    title: COMMON_COPY.pageTitles.analytics,
  },
  { test: (pathname: string) => pathname === "/muggleTestV0/dashboard/account", title: COMMON_COPY.pageTitles.account },
  {
    test: (pathname: string) => pathname === "/muggleTestV0/dashboard/schedule",
    title: COMMON_COPY.pageTitles.schedule,
  },
  {
    test: (pathname: string) => pathname === "/muggleTestV0/dashboard/organization",
    title: COMMON_COPY.pageTitles.organization,
  },
  {
    test: (pathname: string) => pathname === "/muggleTestV0/dashboard/organization/members",
    title: COMMON_COPY.pageTitles.organizationMembers,
  },
  {
    test: (pathname: string) => pathname === "/muggleTestV0/dashboard/organization/capacity",
    title: COMMON_COPY.pageTitles.organizationCapacity,
  },
  {
    test: (pathname: string) => pathname === "/muggleTestV0/dashboard/organization/billing",
    title: COMMON_COPY.pageTitles.organizationBilling,
  },
  {
    test: (pathname: string) => pathname === "/muggleTestV0/dashboard/organization/usage",
    title: COMMON_COPY.pageTitles.organizationUsage,
  },
  {
    test: (pathname: string) => pathname === "/muggleTestV0/org-invite",
    title: COMMON_COPY.pageTitles.acceptOrganizationInvitation,
  },
  { test: (pathname: string) => pathname === "/muggleTestV0/project-transfer", title: "Your Muggle Test Project" },
  {
    test: (pathname: string) => pathname === "/muggleTestV0/dashboard/admin/project-transfers",
    title: "Project Transfers",
  },
  {
    test: (pathname: string) => pathname === PRICING_CASES_ADMIN_PATH,
    title: COMMON_COPY.pageTitles.pricingCases,
  },
  {
    test: (pathname: string) => pathname === PARTNER_PAGE_PATH,
    title: PARTNER_PROGRAM_TITLE,
  },
  {
    test: (pathname: string) => pathname === ADMIN_PARTNERS_PATH,
    title: ADMIN_PARTNERS_TITLE,
  },
  {
    test: (pathname: string) => pathname === ADMIN_REFERRALS_PATH,
    title: ADMIN_REFERRALS_TITLE,
  },
  {
    test: (pathname: string) => pathname === ADMIN_PARTNER_STATEMENTS_PATH,
    title: ADMIN_PARTNER_STATEMENTS_TITLE,
  },
  {
    test: (pathname: string) => pathname === ADMIN_SOURCE_OF_SALES_PATH,
    title: ADMIN_SOURCE_OF_SALES_TITLE,
  },
  {
    test: (pathname: string) => pathname === ADMIN_SUBSCRIPTIONS_PATH,
    title: ADMIN_SUBSCRIPTIONS_TITLE,
  },
  {
    test: (pathname: string) => pathname === "/muggleTestV0/dashboard/my-orgs",
    title: COMMON_COPY.pageTitles.enterpriseBilling,
  },
  // Charity fundraiser routes
  { test: (pathname: string) => pathname === "/charity", title: "Ride to Rebuild" },
  { test: (pathname: string) => pathname === "/charity/join", title: "Join - Ride to Rebuild" },
  { test: (pathname: string) => pathname === "/charity/signin", title: "Sign In - Ride to Rebuild" },
  { test: (pathname: string) => /^\/charity\/auth\/[^/]+$/.test(pathname), title: "Signing In - Ride to Rebuild" },
  { test: (pathname: string) => pathname === "/charity/report", title: CHARITY_REPORT_ACTION_LABEL },
  { test: (pathname: string) => pathname === "/charity/reports", title: CHARITY_RIDES_LABEL },
  { test: (pathname: string) => /^\/charity\/reports\/[^/]+$/.test(pathname), title: "Report Details" },
  { test: (pathname: string) => pathname === "/charity/household", title: "Household" },
  { test: (pathname: string) => pathname === "/charity/leaderboard", title: "Leaderboards" },
  {
    test: (pathname: string) => pathname === "/charity/how-it-works",
    title: "How to take part - Ride to Rebuild",
  },
  {
    test: (pathname: string) => pathname === "/charity/how-it-works/joining",
    title: "Joining, step by step - Ride to Rebuild",
  },
  {
    test: (pathname: string) => pathname === "/charity/how-it-works/apps",
    title: "Screenshot guide by app - Ride to Rebuild",
  },
  {
    test: (pathname: string) => pathname === "/charity/how-it-works/reporting",
    title: "Reporting a ride, step by step - Ride to Rebuild",
  },
  {
    test: (pathname: string) => pathname === "/charity/how-it-works/group-rides",
    title: "The group ride, in detail - Ride to Rebuild",
  },
  {
    test: (pathname: string) => pathname === "/charity/ride-together",
    title: "Ride together - Ride to Rebuild",
  },
  { test: (pathname: string) => pathname === "/charity/rules", title: "Rules - Ride to Rebuild" },
  { test: (pathname: string) => pathname === "/charity/privacy", title: "Privacy - Ride to Rebuild" },
  { test: (pathname: string) => pathname === "/charity/admin", title: "Review Queue - Organizer" },
  {
    test: (pathname: string) => /^\/charity\/admin\/reports\/[^/]+$/.test(pathname),
    title: "Review Submission - Organizer",
  },
  { test: (pathname: string) => pathname === "/charity/admin/export", title: "Closeout Export - Organizer" },
];

// Wrapper components to pass params to dynamic route components
const ProjectDetailWrapper: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  if (!id) throw new Error("Project ID is required");
  return <ProjectDetailPage params={{ id }} />;
};

const ProjectTestcasesWrapper: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  if (!id) throw new Error("Project ID is required");
  return <ProjectTestcasesPage params={{ id }} />;
};

const ProjectSchedulesWrapper: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  if (!id) throw new Error("Project ID is required");
  return <ProjectSchedulesPage params={{ id }} />;
};

const ProjectReportWrapper: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  if (!id) throw new Error("Project ID is required");

  const reportView = searchParams.get("view");
  if (reportView === "new") {
    return (
      <Suspense fallback={<PageLoader />}>
        <TestReportPage />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <LegacyProjectReportPage params={{ id }} />
    </Suspense>
  );
};

/**
 * Wrapper that guards protected routes behind session initialization.
 * If session init is still loading, shows a page loader.
 * If session init failed, shows the backend-unavailable page (with sign-out option).
 * Otherwise, renders the child component normally.
 */
const SessionGuardedDashboardLayout: React.FC = () => {
  const { user } = useAuth0();
  const { isLoading: isSessionLoading, error: sessionInitializationError } = useUserSessionInitializer();

  if (isSessionLoading) {
    return <PageLoader />;
  }

  if (sessionInitializationError) {
    return (
      <MuiThemeProvider theme={darkTheme}>
        <CssBaseline />
        <BackendUnavailablePage error={sessionInitializationError} userEmail={user?.email} userId={user?.sub} />
      </MuiThemeProvider>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <DashboardLayout />
    </Suspense>
  );
};

/**
 * Picks the tab icon for the route being viewed.
 *
 * The campaign is its own brand living under the same origin, so charity routes
 * carry the campaign mark rather than the platform's avatar.
 *
 * @param pathname - The current location pathname
 * @returns Public path of the icon the tab should show
 */
function resolveAppIconPath(pathname: string): string {
  return pathname.startsWith(CHARITY_ROUTE_PREFIX) ? CAMPAIGN_ICON_PATH : APP_ICON_PATH;
}

/**
 * Picks the manifest describing the route being viewed as an installed app.
 *
 * Without this a rider who adds a charity route to their home screen installs
 * the platform's manifest — the platform's name, and a start url landing them
 * in the testing product rather than on their ride report.
 *
 * @param pathname - The current location pathname
 * @returns Public path of the manifest for this route
 */
function resolveManifestPath(pathname: string): string {
  return pathname.startsWith(CHARITY_ROUTE_PREFIX) ? CAMPAIGN_MANIFEST_PATH : APP_MANIFEST_PATH;
}

/**
 * Picks the label iOS writes under the home-screen icon.
 *
 * iOS prefers this to the document title, which ends in the platform's name on
 * every route. Empty on platform routes, where the title already says the right
 * thing and a second source of the same name is one more thing to keep in step.
 *
 * @param pathname - The current location pathname
 * @returns The home-screen label, or an empty string to leave the title to speak
 */
function resolveHomeScreenTitle(pathname: string): string {
  return pathname.startsWith(CHARITY_ROUTE_PREFIX) ? CAMPAIGN_HOME_SCREEN_TITLE : "";
}

function resolvePageTitle({ pathname, copy }: { pathname: string; copy: ICopyResolver }): string {
  const matchedRule = TITLE_RULES.find((rule: ITitleRule) => rule.test(pathname));
  if (!matchedRule) {
    return APP_NAME;
  }

  const ruleTitle = typeof matchedRule.title === "string" ? matchedRule.title : copy.text(matchedRule.title);
  return `${ruleTitle} | ${APP_NAME}`;
}

function ensureHeadLinkElement({ rel, id }: { rel: string; id?: string }): HTMLLinkElement {
  // index.html ships its own links with no id, so an id-only lookup misses
  // them on the first pass and appends a rival tag. The stale one then stays in
  // the document declaring a different icon, and which of the two a browser
  // honours is left to chance.
  const identifiedLinkElement = id ? document.head.querySelector(`link#${id}`) : null;
  const existingLinkElement = identifiedLinkElement ?? document.head.querySelector(`link[rel="${rel}"]`);
  if (existingLinkElement instanceof HTMLLinkElement) {
    if (id) {
      existingLinkElement.id = id;
    }
    return existingLinkElement;
  }

  const createdLinkElement = document.createElement("link");
  createdLinkElement.rel = rel;
  if (id) {
    createdLinkElement.id = id;
  }
  document.head.appendChild(createdLinkElement);
  return createdLinkElement;
}

function ensureHeadMetaElement(name: string): HTMLMetaElement {
  const existingMetaElement = document.head.querySelector(`meta[name="${name}"]`);
  if (existingMetaElement instanceof HTMLMetaElement) {
    return existingMetaElement;
  }

  const createdMetaElement = document.createElement("meta");
  createdMetaElement.name = name;
  document.head.appendChild(createdMetaElement);
  return createdMetaElement;
}

const RouteMetadataManager: React.FC = () => {
  const location = useLocation();
  const copy = useCopy();
  const pageTitle = resolvePageTitle({ pathname: location.pathname, copy: copy });

  // Declared before the page-view effect so gtag reads the new title on navigation.
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  useEffect(() => {
    const pagePath = location.pathname + location.search;
    // English and keyed to navigation only, so page reports neither split by language nor count a language switch.
    const analyticsPageTitle = resolvePageTitle({
      pathname: location.pathname,
      copy: createCopyResolver({ locale: DEFAULT_LOCALE }),
    });

    const gtag = window.gtag;
    if (typeof gtag === "function") {
      gtag("config", GOOGLE_ANALYTICS_MEASUREMENT_ID, {
        page_path: pagePath,
      });
    }

    pushPageViewDataLayerEvent({ pagePath: pagePath, pageTitle: analyticsPageTitle });
  }, [location.pathname, location.search]);

  useEffect(() => {
    const iconPath = resolveAppIconPath(location.pathname);

    const faviconLinkElement = ensureHeadLinkElement({ rel: FAVICON_REL, id: FAVICON_ID });
    faviconLinkElement.href = iconPath;
    faviconLinkElement.type = APP_ICON_TYPE;

    const shortcutIconLinkElement = ensureHeadLinkElement({ rel: SHORTCUT_ICON_REL });
    shortcutIconLinkElement.href = iconPath;
    shortcutIconLinkElement.type = APP_ICON_TYPE;

    const appleTouchIconLinkElement = ensureHeadLinkElement({ rel: APPLE_TOUCH_ICON_REL });
    appleTouchIconLinkElement.href = iconPath;
    appleTouchIconLinkElement.type = APP_ICON_TYPE;

    ensureHeadLinkElement({ rel: MANIFEST_REL }).href = resolveManifestPath(location.pathname);

    // Safari reads the head when the rider taps Add to Home Screen, so this has
    // to be right at that moment rather than at first paint. Cleared on the way
    // out rather than left behind, or the campaign's name would follow the rider
    // onto a platform route and install itself there.
    ensureHeadMetaElement(APPLE_WEB_APP_TITLE_NAME).content = resolveHomeScreenTitle(location.pathname);
  }, [location.pathname]);

  return null;
};

function App(): React.ReactElement {
  const { isLoading: isAuthLoading } = useAuth0();
  const { hasUpdate } = useVersionWatcher({ intervalMs: 5_000 });

  if (isAuthLoading) {
    return (
      <div className="page-layout">
        <PageLoader />
      </div>
    );
  }

  return (
    <>
      <ReloadPrompt
        open={hasUpdate}
        onReload={() => {
          // Not a plain navigation: the service worker would answer it from the
          // same precached shell the visitor is trying to get off.
          void reloadWithLatestServiceWorker();
        }}
      />
      <ErrorBoundary>
        <RouteMetadataManager />
        <Routes>
          <Route
            path=""
            element={
              <MuiThemeProvider theme={darkTheme}>
                <CssBaseline />
                <MasterLayout />
              </MuiThemeProvider>
            }
          >
            <Route path="/" element={<Navigate to="/muggleTestV0" replace />} />
            <Route path="terms" element={<Navigate to="/muggleTestV0/terms" replace />} />
            {archivedRoutes}
            <Route path="callback" element={<CallbackPage />} />
          </Route>
          <Route
            path="muggleTestV0"
            element={
              <ThemeProvider defaultTheme="system" enableSystem>
                <Suspense fallback={<PageLoader />}>
                  <MuggleTestLayout />
                </Suspense>
              </ThemeProvider>
            }
          >
            <Route index element={<MainPageV2 />} />
            <Route path="landing" element={<MainPageV2 />} />
            <Route path="invite" element={<InvitePage />} />
            <Route path="org-invite" element={<OrgInviteAcceptPage />} />
            <Route path="project-transfer" element={<ProjectTransferLandingPage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="docs" element={<DocsPage />} />
            <Route path="docs/:sectionId/:articleId" element={<DocsPage />} />
            <Route path="terms" element={<Terms />} />
            <Route path="health" element={<HealthPage />} />
            <Route path="dashboard" element={<ProtectedRoute component={SessionGuardedDashboardLayout} />}>
              <Route index element={<DashboardPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="projects" element={<ProjectsPage />} />
              <Route path="projects/:id" element={<ProjectDetailWrapper />} />
              <Route path="projects/create" element={<CreateProjectLayout />}>
                <Route index element={<Navigate to="website" replace />} />
                <Route path="website" element={<CreateWebsitePage />} />
                <Route path="requirements" element={<CreateRequirementsPage />} />
                <Route path="prd" element={<CreatePrdPage />} />
                <Route path="credentials" element={<CreateCredentialsPage />} />
                <Route path="progress" element={<CreateProgressPage />} />
              </Route>
              <Route path="projects/:id/testcases" element={<ProjectTestcasesWrapper />} />
              <Route path="projects/:id/scripts" element={<TestCasesRedirect />} />
              <Route path="projects/:id/runs" element={<TestCasesRedirect />} />
              <Route path="projects/:id/report" element={<ProjectReportWrapper />} />
              <Route path="projects/:id/schedules" element={<ProjectSchedulesWrapper />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="billing" element={<BillingPage />} />
              <Route
                path="my-orgs"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <EnterpriseBillingPage />
                  </Suspense>
                }
              />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="account" element={<AccountPage />} />
              <Route path="schedule" element={<SchedulePage />} />
              <Route element={<SuspendedOrgRedirect />}>
                <Route path="organization" element={<OrganizationOverviewPage />} />
                <Route path="organization/members" element={<OrganizationMembersPage />} />
                <Route path="organization/capacity" element={<OrganizationCapacityPage />} />
                <Route
                  path="organization/seats"
                  element={<Navigate to="/muggleTestV0/dashboard/organization/capacity" replace />}
                />
                <Route path="organization/billing" element={<OrganizationBillingPage />} />
                <Route path="organization/usage" element={<OrganizationUsagePage />} />
              </Route>
              <Route
                path="partner"
                element={
                  <PartnerRoute>
                    <PartnerPage />
                  </PartnerRoute>
                }
              />
              <Route
                path="admin/users"
                element={
                  <AdminRoute>
                    <AdminUsersPage />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/grant-trial"
                element={
                  <AdminRoute>
                    <AdminGrantTrialPage />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/subscriptions"
                element={
                  <AdminRoute>
                    <AdminSubscriptionsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/project-transfers"
                element={
                  <AdminRoute>
                    <AdminProjectTransfersPage />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/pricing-cases"
                element={
                  <AdminRoute>
                    <AdminPricingCasesPage />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/partners"
                element={
                  <AdminRoute>
                    <AdminPartnersPage />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/referrals"
                element={
                  <AdminRoute>
                    <AdminReferralsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/partner-statements"
                element={
                  <AdminRoute>
                    <AdminPartnerStatementsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/source-of-sales"
                element={
                  <AdminRoute>
                    <AdminSourceOfSalesPage />
                  </AdminRoute>
                }
              />
            </Route>
          </Route>
          <Route
            path="charity"
            element={
              <Suspense fallback={<PageLoader />}>
                <CharityLayout />
              </Suspense>
            }
          >
            <Route index element={<CharityCampaignPage />} />
            <Route path="join" element={<CharityJoinPage />} />
            <Route path="signin" element={<CharitySignInPage />} />
            <Route path="auth/:token" element={<CharityAuthPage />} />
            <Route path="report" element={<CharityReportPage />} />
            <Route path="reports" element={<CharityReportsPage />} />
            <Route path="reports/:id" element={<CharityReportCorrectPage />} />
            <Route path="household" element={<CharityHouseholdPage />} />
            <Route path="leaderboard" element={<CharityLeaderboardPage />} />
            <Route path="how-it-works" element={<CharityHowItWorksPage />} />
            <Route path="how-it-works/joining" element={<CharityHowToJoinPage />} />
            <Route path="how-it-works/apps" element={<CharityHowToScreenshotAppsPage />} />
            <Route path="how-it-works/reporting" element={<CharityHowToReportPage />} />
            <Route path="how-it-works/group-rides" element={<CharityHowToGroupRidePage />} />
            <Route path="ride-together" element={<CharityRideTogetherPage />} />
            <Route path="rules" element={<CharityRulesPage />} />
            <Route path="privacy" element={<CharityPrivacyPage />} />
            <Route
              path="admin"
              element={
                <ProtectedRoute
                  component={() => (
                    <Suspense fallback={<PageLoader />}>
                      <CharityAdminQueuePage />
                    </Suspense>
                  )}
                />
              }
            />
            <Route
              path="admin/reports/:id"
              element={
                <ProtectedRoute
                  component={() => (
                    <Suspense fallback={<PageLoader />}>
                      <CharityAdminReportDetailPage />
                    </Suspense>
                  )}
                />
              }
            />
            <Route
              path="admin/export"
              element={
                <ProtectedRoute
                  component={() => (
                    <Suspense fallback={<PageLoader />}>
                      <CharityAdminExportPage />
                    </Suspense>
                  )}
                />
              }
            />
          </Route>
        </Routes>
      </ErrorBoundary>
    </>
  );
}

export default App;
